import * as React from "react";
import { Add } from "@mui/icons-material";
import { DoseWarningDialog } from "./dose-warning.dialog";
import { QueryObservations } from "../../observation/query-observations";
import { MessageDialog } from "../../../../../components/message-dialog";
import { productModelToDetails } from "../../../../../models/Product.model";
import { TextField, Box, Paper, Typography, CircularProgress, Button, useTheme } from "@mui/material";
import { DoseNotifyDialog } from "./dose-notify.dialog";
import { Preview } from "../../components/preview/preview";
import { NoPatientDialog } from "../../../../../components/message-dialog";
import { SimpleSnackbar } from "../../../../../components/snackbar";
import WizardShell from "../../../../../components/ui/WizardShell";
import { TOPIC_STEPS } from "../../wizard-steps";
import { ProductDetailDrawer } from "../products/product-detail-drawer";
import { productModelToDetails as pmt } from "../../../../../models/Product.model";
import { VehiclesItem } from "./vehicles-item";
import { useDosePage } from "./use-dose-page";
import ActiveDoseRow from "./components/ActiveDoseRow";

export const DosePage: React.FC = () => {
    const theme = useTheme();
    const {
        model,
        hasMultiplePatients,
        open,
        setOpen,
        showAnotherConsult,
        showNotifyDialog,
        setShowNotifyDialog,
        openObservations,
        setOpenObservations,
        previewOpen,
        setPreviewOpen,
        openNoPatientDialog,
        setOpenNoPatientDialog,
        pendingAction,
        loading,
        showSnackbar,
        setShowSnackbar,
        snackbarMessage,
        detailOpen,
        setDetailOpen,
        detailProduct,
        setDetailProduct,
        deleteDialogOpen,
        setDeleteDialogOpen,
        pendingDeleteId,
        setPendingDeleteId,
        observation,
        observations,
        products,
        nameConsultation,
        totalPercentage,
        handleChange,
        handleActionsToDone,
        handleConfirmDialog,
        onDeleteItem,
        handleFinishConsult,
        handleAnotherConsult,
        handleBackButton,
        updateName,
        updateObservation,
        saveObservations,
    } = useDosePage();

    // Footer buttons for WizardShell — com múltiplos pacientes o PDF fica disponível apenas
    // no histórico (uma receita por paciente); aqui só é possível salvar e pré-visualizar.
    const footerExtra = (
        <>
            <Button
                variant="text"
                onClick={() => setPreviewOpen(true)}
                sx={{ height: 48, px: 2.5, color: "secondary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.divider}`, "&:hover": { backgroundColor: theme.palette.brand.surface2 } }}
            >
                Pré-visualizar
            </Button>
            {!hasMultiplePatients && (
                <Button
                    variant="text"
                    onClick={() => handleActionsToDone("pdf")}
                    sx={{ height: 48, px: 2.5, fontWeight: 600, color: "primary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.primary.main}`, "&:hover": { backgroundColor: theme.palette.brand.surface2 } }}
                >
                    Gerar receita
                </Button>
            )}
        </>
    );

    return (
        <WizardShell
            steps={[...TOPIC_STEPS]}
            activeIndex={4}
            onBack={handleBackButton}
            onNext={() => handleActionsToDone("done")}
            nextLabel="Salvar"
            footerExtra={footerExtra}
        >
            <NoPatientDialog
                open={openNoPatientDialog}
                onClose={() => setOpenNoPatientDialog(false)}
                onConfirm={() => {
                    setOpenNoPatientDialog(false);
                    handleActionsToDone(pendingAction, true);
                }}
            />

            <DoseNotifyDialog
                open={showNotifyDialog.show}
                onClose={() => setShowNotifyDialog({ show: false })}
                onConfirm={() => handleActionsToDone(showNotifyDialog.action)}
            />

            <MessageDialog
                open={showAnotherConsult}
                title={"Continuar formulando?"}
                message={"Deseja continuar formulando para o(s) mesmo(s) paciente(s)?"}
                onClose={handleFinishConsult}
                onConfirm={handleAnotherConsult}
                confirmButtonText={"Sim"}
                cancelButtonText={"Não"}
            />

            <DoseWarningDialog open={open} onClose={() => setOpen(false)} onConfirm={handleConfirmDialog} />

            <MessageDialog
                open={deleteDialogOpen}
                title={"Remover ativo?"}
                message={"Tem certeza que deseja remover o Ativo?"}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setPendingDeleteId(null);
                }}
                onConfirm={() => {
                    if (pendingDeleteId !== null) onDeleteItem(pendingDeleteId);
                    setDeleteDialogOpen(false);
                    setPendingDeleteId(null);
                }}
                confirmButtonText={"Deletar"}
                cancelButtonText={"Cancelar"}
            />

            {loading && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 2000,
                    }}
                >
                    <CircularProgress size={60} color="primary" />
                </Box>
            )}

            <SimpleSnackbar show={showSnackbar} message={snackbarMessage} onDismiss={() => setShowSnackbar(false)} />

            <QueryObservations
                open={openObservations}
                externalObservations={observations}
                onClose={() => setOpenObservations(false)}
                onConfirm={saveObservations}
            />

            <Preview
                observations={model.observations}
                observation={model.observation}
                vehicle={model.vehicles}
                patientId={model.patientId}
                patientIds={model.patientIds}
                period={model.period}
                consultationName={nameConsultation}
                details={products.map(productModelToDetails)}
                previewOpen={previewOpen}
                onClose={() => setPreviewOpen(false)}
                onConfirm={handleConfirmDialog}
            />

            {detailProduct && (
                <ProductDetailDrawer open={detailOpen} detail={pmt(detailProduct)} onClose={() => setDetailOpen(false)} />
            )}

            {/* Consultation name + total */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
                <TextField
                    label={"Nome da consulta"}
                    sx={{ flex: 1 }}
                    color={"primary"}
                    value={nameConsultation}
                    onChange={(e) => updateName(e.target.value)}
                    inputProps={{ maxLength: 100 }}
                    helperText={`${nameConsultation.length}/${100}`}
                />

                <Paper
                    elevation={0}
                    sx={{
                        px: 1.5,
                        height: 56,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 2,
                        textAlign: "center",
                        minWidth: "72px",
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.brand.cream,
                    }}
                >
                    <Typography sx={{ fontSize: "17px", fontWeight: 600, lineHeight: 1.1, color: "primary.main" }}>{totalPercentage}%</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10.5px", lineHeight: 1.1 }}>
                        Conc. total
                    </Typography>
                </Paper>
            </Box>

            {/* Actives list */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                {products.map((product) => (
                    <ActiveDoseRow
                        key={product.idProduct}
                        product={product}
                        onConcentrationChange={handleChange}
                        onInfo={(p) => {
                            setDetailProduct(p);
                            setDetailOpen(true);
                        }}
                        onDelete={(p) => {
                            setPendingDeleteId(p.idProduct ?? null);
                            setDeleteDialogOpen(true);
                        }}
                    />
                ))}
            </Box>

            {/* Vehicle observation (topic only) */}
            {model.vehicles && (
                <Box sx={{ mb: 2 }}>
                    <VehiclesItem vehicles={model.vehicles} observation={observation} onObservationChange={updateObservation} />
                </Box>
            )}

            {/* Observations button */}
            <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setOpenObservations(true)}
                sx={{ height: 44, px: 2.5, borderColor: theme.palette.divider, color: "text.secondary", mb: 1 }}
            >
                Observações
            </Button>
        </WizardShell>
    );
};
